import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Content, ContentWrapper } from '../Content';
import * as Styled from './AboutApp.styles';
import { cards as cardsData, phoneImg } from './data';

const AboutApp = () => {
  const { t } = useTranslation();

  const cards = useMemo(
    () =>
      cardsData.map((card) => ({
        ...card,
        title: t(`homeAboutApp.cards.${card.id}.title`),
        text: t(`homeAboutApp.cards.${card.id}.text`),
        screen: {
          ...card.screen,
          alt: t(`homeAboutApp.cards.${card.id}.screenAlt`),
        },
      })),
    [t],
  );

  const [activeCardId, setActiveCardId] = useState(cards[0].id);

  const { screen } = cards.find((card) => card.id === activeCardId);

  return (
    <Styled.AboutApp id="about-app">
      <ContentWrapper>
        <Content
          labels={[t('homeAboutApp.label')]}
          title={t('homeAboutApp.title')}
        />

        <Styled.Bottom>
          <Styled.Phone>
            <Styled.PhoneImg
              src={phoneImg.src}
              alt={t('homeAboutApp.phoneAlt')}
            />

            <AnimatePresence mode="popLayout">
              <Styled.Screen
                key={activeCardId}
                src={screen.src}
                alt={screen.alt}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.04 }}
                transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              />
            </AnimatePresence>
          </Styled.Phone>

          <Styled.Cards>
            {cards.map(({ id, title, text }) => (
              <Styled.Card
                key={id}
                className={cn('glass-border', activeCardId === id && 'active')}
                onClick={() => setActiveCardId(id)}
              >
                <Styled.Title variant="h5">
                  <Styled.Decor className="decor">
                    {activeCardId === id && (
                      <>
                        <Styled.Pulse
                          initial={{ scale: 1, opacity: 0 }}
                          animate={{ scale: [1, 1.5], opacity: [0, 0.5, 0] }}
                          transition={{
                            duration: 2.6,
                            repeat: Infinity,
                            ease: 'easeOut',
                          }}
                        />
                        <Styled.Pulse
                          initial={{ scale: 1, opacity: 0 }}
                          animate={{ scale: [1, 1.5], opacity: [0, 0.5, 0] }}
                          transition={{
                            duration: 2.6,
                            repeat: Infinity,
                            ease: 'easeOut',
                            delay: 1.3,
                          }}
                        />
                      </>
                    )}
                  </Styled.Decor>
                  {title}
                </Styled.Title>

                <Styled.Text variant="body16" className="text">
                  {text}
                </Styled.Text>
              </Styled.Card>
            ))}
          </Styled.Cards>

          <Styled.Decors>
            <Styled.Circle />
            <Styled.Circle />
          </Styled.Decors>
        </Styled.Bottom>
      </ContentWrapper>
    </Styled.AboutApp>
  );
};

export default AboutApp;
